/**
 * arbi config set-url <url>
 * arbi config show
 * arbi config alias
 */

import { existsSync, readFileSync, appendFileSync } from 'fs'
import { homedir } from 'os'
import { join } from 'path'
import { Command } from 'commander'
import { getConfig, updateConfig } from '../config.js'
import { success, error, label, dim } from '../output.js'

const ALIAS_LINE = 'alias A="arbi ask"'
const ALIAS_MARKER = '# arbi-cli alias'

function getShellRcPath(): string {
  const shell = process.env.SHELL || ''
  if (shell.includes('zsh')) return join(homedir(), '.zshrc')
  return join(homedir(), '.bashrc')
}

function isAliasInstalled(rcPath: string): boolean {
  if (!existsSync(rcPath)) return false
  const content = readFileSync(rcPath, 'utf-8')
  return content.includes(ALIAS_LINE) || content.includes(ALIAS_MARKER)
}

export function registerConfigCommand(program: Command): void {
  const config = program
    .command('config')
    .description('CLI configuration: set-url, show, notifications, verbose, watch, alias')

  config
    .command('set-url <url>')
    .description('Set the ARBI server URL')
    .action((url: string) => {
      try {
        const parsed = new URL(url)
        const deploymentDomain = parsed.hostname
        updateConfig({ baseUrl: url.replace(/\/+$/, ''), deploymentDomain })
        label('Server URL:', url)
        label('Domain:', deploymentDomain)
      } catch {
        error(`Invalid URL: ${url}`)
        process.exit(1)
      }
    })

  config
    .command('show')
    .description('Show current configuration')
    .option('--json', 'Output as JSON')
    .action((opts: { json?: boolean }) => {
      const cfg = getConfig()
      if (opts.json) {
        // Project the same fields the plain view shows, so an agent piping
        // `arbi config show --json` doesn't have to learn a second schema.
        // Previously plain showed Verbose/Watch but JSON omitted them, and
        // JSON exposed `orchestrator` that plain never mentioned. Now both
        // surfaces agree.
        const projection = cfg
          ? {
              baseUrl: cfg.baseUrl ?? null,
              deploymentDomain: cfg.deploymentDomain ?? null,
              autoUpdate: cfg.autoUpdate ?? false,
              verbose: cfg.verbose !== false,
              watch: cfg.watch !== false,
              notifications: cfg.notifications !== false,
              orchestrator: cfg.orchestrator ?? null,
              selectedWorkspaceId: cfg.selectedWorkspaceId ?? null,
            }
          : {}
        console.log(JSON.stringify(projection, null, 2))
        return
      }
      if (!cfg) {
        dim('Not configured. Run: arbi config set-url <url>')
        return
      }
      label('Server URL:', cfg.baseUrl)
      label('Domain:', cfg.deploymentDomain)
      label('Auto-update:', cfg.autoUpdate ? 'on' : 'off')
      label('Verbose:', cfg.verbose !== false ? 'on' : 'off')
      label('Watch:', cfg.watch !== false ? 'on' : 'off')
      label('Notifications:', cfg.notifications !== false ? 'on' : 'off')
      // Surface orchestrator in plain output too — it's the only setting that
      // was hidden from humans but visible to scripts.
      if (cfg.orchestrator) label('Orchestrator:', cfg.orchestrator)
      if (cfg.selectedWorkspaceId) {
        label('Workspace:', cfg.selectedWorkspaceId)
      }
    })

  config
    .command('notifications [on|off]')
    .description('Toggle background WebSocket notifications')
    .action((toggle?: string) => {
      const cfg = getConfig()
      if (!toggle) {
        label('Notifications:', cfg?.notifications !== false ? 'on' : 'off')
        return
      }
      if (toggle !== 'on' && toggle !== 'off') {
        error('Usage: arbi config notifications [on|off]')
        process.exit(1)
      }
      updateConfig({ notifications: toggle === 'on' })
      success(`Notifications: ${toggle}`)
    })

  config
    .command('verbose [on|off]')
    .description('Toggle verbose agent steps in ask (default: on)')
    .action((toggle?: string) => {
      const cfg = getConfig()
      if (!toggle) {
        label('Verbose:', cfg?.verbose !== false ? 'on' : 'off')
        return
      }
      if (toggle !== 'on' && toggle !== 'off') {
        error('Usage: arbi config verbose [on|off]')
        process.exit(1)
      }
      updateConfig({ verbose: toggle === 'on' })
      success(`Verbose: ${toggle}`)
    })

  config
    .command('watch [on|off]')
    .description('Toggle upload watch (default: on)')
    .action((toggle?: string) => {
      const cfg = getConfig()
      if (!toggle) {
        label('Watch:', cfg?.watch !== false ? 'on' : 'off')
        return
      }
      if (toggle !== 'on' && toggle !== 'off') {
        error('Usage: arbi config watch [on|off]')
        process.exit(1)
      }
      updateConfig({ watch: toggle === 'on' })
      success(`Watch: ${toggle}`)
    })

  config
    .command('alias')
    .description('Append `alias A="arbi ask"` to your shell rc file (idempotent, reversible)')
    .action(() => {
      const rcPath = getShellRcPath()

      if (isAliasInstalled(rcPath)) {
        success(`Alias already set up in ${rcPath}`)
        dim('Usage: A what is the meaning of life')
        return
      }

      appendFileSync(rcPath, `\n${ALIAS_MARKER}\n${ALIAS_LINE}\n`)
      success(`Added alias A="arbi ask" to ${rcPath}`)
      dim('')
      dim(`Run: source ${rcPath}`)
      dim('Then: A what is the meaning of life')
    })
}
